import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Store,
  Crown,
  Shield,
  UserCheck,
  Clock,
  CheckCircle,
  Circle,
  Package,
  ShoppingCart,
  Edit,
  LogIn,
  ExternalLink,
  Globe
} from 'lucide-react';
import api from '@/utils/api';
import { AdminPageSkeleton } from '@/components/admin/AdminPageSkeleton';
import { toast } from 'sonner';
import { isPaidPlan, planLabel } from '@/lib/plan';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TimelineEvent {
  type: 'onboarding' | 'store_created' | 'first_product' | 'first_order' | 'profile_edit' | 'login' | 'first_booking';
  title: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

interface UserStore {
  _id: string;
  storeName: string;
  businessName: string;
  url: string;
  logo: string;
  createdAt: string;
}

interface UserDetails {
  _id: string;
  name: string;
  email: string;
  role: string;
  whatsappNumber?: string;
  onboardingStatus: string;
  plan?: 'free' | 'premium' | 'custom' | 'growth';
  nextPaymentDueAt?: string | null;
  nextPaymentInterval?: string | null;
  createdAt: string;
  lastLoginAt?: string;
  stores: UserStore[];
  timeline: TimelineEvent[];
  stats: {
    totalStores: number;
    totalProducts: number;
    totalOrders: number;
    totalBookings: number;
  };
}

export function UserDetailsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [planSaving, setPlanSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/platform-analytics/users/${userId}`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const updateUserPlan = async (newPlan: 'free' | 'premium' | 'custom' | 'growth') => {
    if (!userId) return;
    setPlanSaving(true);
    try {
      await api.patch(`/api/platform-analytics/users/${userId}/plan`, { plan: newPlan });
      setUser((prev) => (prev ? { ...prev, plan: newPlan } : null));
      toast.success('Plan updated');
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!userId) return;
    setDeletingAccount(true);
    try {
      await api.delete(`/api/platform-analytics/users/${userId}`);
      toast.success('Account deleted successfully');
      navigate('/admin/users');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
      setShowDeleteDialog(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superadmin':
        return <Crown className="h-4 w-4" />;
      case 'owner':
        return <Shield className="h-4 w-4" />;
      case 'manager':
        return <UserCheck className="h-4 w-4" />;
      case 'customer':
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'destructive';
      case 'owner':
        return 'default';
      case 'manager':
        return 'secondary';
      case 'customer':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getOnboardingBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'pending':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'onboarding':
        return <CheckCircle className="h-5 w-5" />;
      case 'store_created':
        return <Store className="h-5 w-5" />;
      case 'first_product':
        return <Package className="h-5 w-5" />;
      case 'first_order':
        return <ShoppingCart className="h-5 w-5" />;
      case 'first_booking':
        return <Calendar className="h-5 w-5" />;
      case 'profile_edit':
        return <Edit className="h-5 w-5" />;
      case 'login':
        return <LogIn className="h-5 w-5" />;
      default:
        return <Circle className="h-5 w-5" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <AdminPageSkeleton variant="detail" kpiCount={3} />;
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">User not found</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/admin/users')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 md:h-20 md:w-20">
              <AvatarImage src="" alt={user.name} />
              <AvatarFallback className="text-2xl">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{user.name || 'Unknown User'}</h1>
              <p className="text-sm md:text-base text-muted-foreground">{user.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge variant={getRoleBadgeVariant(user.role)} className="flex items-center gap-1">
                  {getRoleIcon(user.role)}
                  {user.role}
                </Badge>
                <Badge variant={getOnboardingBadgeVariant(user.onboardingStatus)}>
                  {user.onboardingStatus}
                </Badge>
                {user.plan && (
                  <Badge variant={isPaidPlan(user.plan) ? 'default' : 'secondary'}>
                    {isPaidPlan(user.plan) && <Crown className="h-3 w-3 mr-1" />}
                    {planLabel(user.plan)} plan
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <Button 
          variant="outline"
          onClick={() => window.open(`mailto:${user.email}`)}
          className="w-full md:w-auto"
        >
          <Mail className="h-4 w-4 mr-2" />
          Contact User
        </Button>
      </div>

      {/* Stats Cards */}
      <Card className="p-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 w-full">
          <Card className="p-4 shadow-none border-none bg-purple-100/30 hover:bg-purple-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Stores</p>
                <div className="font-heading text-lg font-medium text-black">{user.stats.totalStores}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Store className="h-4 w-4 text-purple-500" />
                  <span className="text-sm text-purple-600 font-medium">Owned</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-orange-100/30 hover:bg-orange-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Products</p>
                <div className="font-heading text-lg font-medium text-black">{user.stats.totalProducts}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Package className="h-4 w-4 text-orange-500" />
                  <span className="text-sm text-orange-600 font-medium">Created</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-blue-100/30 hover:bg-blue-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Orders</p>
                <div className="font-heading text-lg font-medium text-black">{user.stats.totalOrders}</div>
                <div className="flex items-center gap-1 mt-1">
                  <ShoppingCart className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-blue-600 font-medium">Received</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-teal-100/30 hover:bg-teal-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Bookings</p>
                <div className="font-heading text-lg font-medium text-black">{user.stats.totalBookings}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="h-4 w-4 text-teal-500" />
                  <span className="text-sm text-teal-600 font-medium">Total</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* User Information and Stores */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        {/* User Information */}
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>Account details and contact information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Full Name</p>
                  <p className="text-sm text-muted-foreground">{user.name || 'Not provided'}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground break-all">{user.email}</p>
                </div>
              </div>

              <Separator />

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium">Subscription plan</p>
                <Select
                  value={user.plan || 'free'}
                  disabled={planSaving}
                  onValueChange={(v) =>
                    updateUserPlan(v as 'free' | 'premium' | 'custom' | 'growth')
                  }
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="growth">Growth (legacy)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {user.plan === 'premium' && user.nextPaymentDueAt && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Next payment due</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(user.nextPaymentDueAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {user.whatsappNumber && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">WhatsApp</p>
                      <p className="text-sm text-muted-foreground">{user.whatsappNumber}</p>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Member Since</p>
                  <p className="text-sm text-muted-foreground">{formatDate(user.createdAt)}</p>
                </div>
              </div>

              {user.lastLoginAt && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <LogIn className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Last Login</p>
                      <p className="text-sm text-muted-foreground">{formatDateTime(user.lastLoginAt)}</p>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {user.role !== 'superadmin' && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full sm:w-auto"
                  >
                    Delete account
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Stores */}
        <Card className="p-4">
          <p className="text-sm font-semibold mb-1">User Stores</p>
          <p className="text-xs text-muted-foreground mb-3">
            {user.stores.length > 0 
              ? `This user owns ${user.stores.length} store${user.stores.length > 1 ? 's' : ''}`
              : 'This user has no stores yet'
            }
          </p>
          {user.stores.length > 0 ? (
            <div className="space-y-3">
              {user.stores.map((store) => (
                <div
                  key={store._id}
                  className="flex items-center justify-between p-2 bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors border border-transparent hover:border-muted-foreground/10"
                >
                  <div className="flex items-center gap-3">
                    {store.logo ? (
                      <img
                        src={store.logo}
                        alt={store.storeName}
                        className="w-8 h-8 rounded-lg object-cover ring-1 ring-primary/10"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center ring-1 ring-primary/10">
                        <Store className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-sm truncate max-w-[150px]">{store.storeName}</div>
                      <div className="text-xs text-muted-foreground">{store.businessName}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => window.open(`/${store.url}`, '_blank')}
                      className="h-auto p-0 text-xs"
                    >
                      <Globe className="h-3 w-3" />
                    </Button>
                    <Link to={`/admin/stores/${store._id}`}>
                      <Button variant="outline" size="sm" className="text-xs">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Store className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No stores created yet</p>
            </div>
          )}
        </Card>
      </div>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>Complete history of user activities and milestones</CardDescription>
        </CardHeader>
        <CardContent>
          {user.timeline.length > 0 ? (
            <div className="space-y-0">
              {user.timeline.map((event, index) => (
                <div key={index} className="relative pb-8 last:pb-0">
                  {index !== user.timeline.length - 1 && (
                    <div className="absolute left-[1.125rem] top-8 bottom-0 w-0.5 bg-border" />
                  )}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      {getTimelineIcon(event.type)}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                        <h4 className="font-semibold text-sm">{event.title}</h4>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateTime(event.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{event.description}</p>
                      {event.metadata && event.metadata.storeName && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <Store className="h-3 w-3 mr-1" />
                            {event.metadata.storeName}
                          </Badge>
                          {event.metadata.storeUrl && (
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => window.open(`/${event.metadata.storeUrl}`, '_blank')}
                              className="h-auto p-0 text-xs"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Store
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No activity recorded yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user and all data for their owned stores, including
              products, orders, booking data, and customers tied to the account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAccount}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAccount}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingAccount}
            >
              {deletingAccount ? 'Deleting...' : 'Delete account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
